import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error("Failed to fetch user:", error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    if (token) {
      await fetchUser();
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    
    localStorage.setItem("token", newToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    
    return response.data;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    return response.data;
  };

  const verify = async (email, code) => {
    const response = await axios.post(`${API}/auth/verify`, { email, code });
    const { token: newToken, user: userData } = response.data;
    
    if (newToken) {
      localStorage.setItem("token", newToken);
      axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
      setToken(newToken);
      setUser(userData);
    }
    
    return response.data;
  };

  const resendCode = async (email) => {
    const response = await axios.post(`${API}/auth/resend-code`, { email, password: "" });
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
  };

  // Subscription helpers
  const canDownload = () => {
    return user?.subscription?.features?.can_download ?? false;
  };

  const canShare = () => {
    return user?.subscription?.features?.can_share ?? false;
  };

  const canCompleteTasks = () => {
    return user?.subscription?.features?.can_complete_tasks ?? false;
  };

  const getMeetingLimit = () => {
    return user?.subscription?.features?.meetings_per_month ?? 1;
  };

  const getMeetingsUsed = () => {
    return user?.usage?.used ?? 0;
  };

  const canCreateMeeting = () => {
    return user?.usage?.can_create ?? true;
  };

  const getPlanId = () => {
    return user?.subscription?.plan_id ?? "free";
  };

  const getPlanName = () => {
    return user?.subscription?.plan_name ?? "Gratuito";
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      verify, 
      resendCode, 
      logout,
      refreshUser,
      // Subscription helpers
      canDownload,
      canShare,
      canCompleteTasks,
      getMeetingLimit,
      getMeetingsUsed,
      canCreateMeeting,
      getPlanId,
      getPlanName
    }}>
      {children}
    </AuthContext.Provider>
  );
};
